import type { WizardCtx } from './types';

export function StepLayout({
  eyebrow, title, sub, children, hero, footer, narrow,
}: {
  eyebrow?: string;
  title?: React.ReactNode;
  sub?: React.ReactNode;
  children?: React.ReactNode;
  hero?: React.ReactNode;
  footer?: React.ReactNode;
  narrow?: boolean;
}) {
  return (
    <div className={`ob-step ${hero ? 'ob-step--hero' : ''}`}>
      <div className={`ob-step-body ${narrow ? 'ob-step-body--narrow' : ''}`}>
        {eyebrow && <div className="ob-eyebrow">{eyebrow}</div>}
        {title && <h1 className="ob-title sp-display">{title}</h1>}
        {sub && <p className="ob-sub">{sub}</p>}
        <div className="ob-content">{children}</div>
        {footer && <div className="ob-footer">{footer}</div>}
      </div>
      {hero && <div className="ob-hero">{hero}</div>}
    </div>
  );
}

export function PrimaryBtn({
  children, onClick, disabled, full,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  full?: boolean;
}) {
  return (
    <button
      type="button"
      className="ob-btn ob-btn--primary"
      onClick={onClick}
      disabled={disabled}
      style={full ? { width: '100%' } : undefined}
    >
      {children} <ArrowRightIcon />
    </button>
  );
}

export function GhostBtn({
  children, onClick, disabled, full,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  full?: boolean;
}) {
  return (
    <button
      type="button"
      className="ob-btn ob-btn--ghost"
      onClick={onClick}
      disabled={disabled}
      style={full ? { width: '100%' } : undefined}
    >
      {children}
    </button>
  );
}

// ── Inline SVG icons ───────────────────────────────────────────
export function ArrowRightIcon({ size = 14 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 16 16" fill="none"><path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>;
}
export function CheckIcon({ size = 14 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 16 16" fill="none"><path d="M3 8.5L6.5 12L13 4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>;
}
export function HeartIcon({ size = 16 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none"><path d="M12 20s-7-4.5-7-10a4 4 0 017-2.5A4 4 0 0119 10c0 5.5-7 10-7 10z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/></svg>;
}
export function ChatIcon({ size = 16 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none"><path d="M21 12a8 8 0 01-11.5 7.2L4 21l1.8-5.5A8 8 0 1121 12z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/></svg>;
}
export function CoinIcon({ size = 16 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6"/><path d="M12 7v10M9.5 9.5h4a1.5 1.5 0 010 3h-3a1.5 1.5 0 000 3h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>;
}
export function LockIcon({ size = 14 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none"><rect x="4" y="11" width="16" height="10" rx="2" stroke="currentColor" strokeWidth="1.6"/><path d="M8 11V7a4 4 0 118 0v4" stroke="currentColor" strokeWidth="1.6"/></svg>;
}
export function CameraIcon({ size = 22 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none"><path d="M4 8h3l2-2h6l2 2h3v11H4V8z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/><circle cx="12" cy="13" r="3.5" stroke="currentColor" strokeWidth="1.6"/></svg>;
}
export function SparkleIcon({ size = 16 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none"><path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3z" fill="currentColor"/></svg>;
}
export function PhoneIcon({ size = 16 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none"><rect x="7" y="3" width="10" height="18" rx="2" stroke="currentColor" strokeWidth="1.6"/><path d="M11 18h2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>;
}
export function AppleIcon({ size = 16 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M16.4 12.2c0-2.4 2-3.6 2-3.7-1.1-1.6-2.8-1.8-3.4-1.8-1.4-.1-2.8.8-3.5.8-.7 0-1.9-.8-3.1-.8-1.6 0-3 .9-3.8 2.4-1.6 2.8-.4 7 1.2 9.3.8 1.1 1.7 2.4 2.9 2.3 1.2-.1 1.6-.7 3-.7 1.4 0 1.8.7 3.1.7 1.3 0 2.1-1.1 2.9-2.2.9-1.3 1.3-2.5 1.3-2.6-.1-.1-2.6-1-2.6-3.7zM14 4.6c.6-.8 1.1-1.9 1-3-1 0-2.1.7-2.7 1.4-.6.7-1.2 1.8-1 2.8 1.1.1 2.2-.5 2.7-1.2z"/></svg>;
}
export function GoogleIcon({ size = 16 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.5-5.9 7.8-11.3 7.8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.1 5.7 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16.1 19 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34.1 7.7 29.3 6 24 6c-7.6 0-14.1 4.3-17.7 10.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35 26.7 36 24 36c-5.4 0-9.9-3.4-11.3-8L6 32.9C9.5 39.5 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.7 2-2 3.7-3.6 5l6.2 5.2C42 35.4 44 30 44 24c0-1.2-.1-2.4-.4-3.5z"/></svg>;
}
export function Spinner({ size = 12 }: { size?: number }) {
  return <svg className="ob-spinner" width={size} height={size} viewBox="0 0 16 16"><circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.6" fill="none" strokeDasharray="20" strokeDashoffset="10"/></svg>;
}
